/**
 * src/pages/venue/VenueEnterPage.tsx — 2304vb
 *
 * Gate into venue-mode. Flow when navigating directly to /venue/:slug/*:
 *   1. NDA splash — acknowledge
 *   2. Auto-create session (no code needed for known slug) → digest
 *
 * Flow when no slug known:
 *   1. NDA splash
 *   2. Code entry → validate → create session → digest
 */

import { useState, type FormEvent } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { KeyRound, ArrowRight } from 'lucide-react'
import { NDASplash } from '@/app/venue/NDASplash'
import {
  validateAccessCode,
  createPickerSession,
  setPickerSession,
  getPickerSession,
  createDemoSession,
} from '@/lib/venue'
import {
  type VenueRole,
  ROLE_CONFIGS,
  getStoredRole,
  setStoredRole,
} from '@/lib/venue-role'

type Stage = 'nda' | 'code' | 'submitting' | 'role'

export function VenueEnterPage() {
  const { slug }   = useParams<{ slug?: string }>()
  const navigate   = useNavigate()
  const [stage, setStage] = useState<Stage>(() => {
    // Skip NDA if session already exists — only show it on first entry
    if (slug && getPickerSession(slug)) return 'role'
    return 'nda'
  })
  const [code, setCode]   = useState('')
  const [name, setName]   = useState('')
  const [error, setError] = useState<string | null>(null)

  // ── NDA accept ─────────────────────────────────────────────────────────────
  // If we have a slug, skip code entry — auto-create a session and go straight in.

  async function onNDAAccept() {
    if (slug) {
      const storedRole = getStoredRole(slug)
      const existing   = getPickerSession(slug)

      // Always ensure session exists
      if (!existing) await createDemoSession(slug)

      // If role already set — go straight to their workspace
      if (storedRole) {
        const config = ROLE_CONFIGS[storedRole]
        navigate(`/venue/${slug}/${config.defaultTab}`, { replace: true })
        return
      }

      // No role stored — show role selector
      setStage('role')
      return
    }
    setStage('code')
  }

  async function onRoleSelect(role: VenueRole) {
    if (!slug) return
    setStoredRole(slug, role)
    const config = ROLE_CONFIGS[role]
    navigate(`/venue/${slug}/${config.defaultTab}`, { replace: true })
  }

  // ── Code submit ────────────────────────────────────────────────────────────

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmedCode = code.trim().toUpperCase()
    const trimmedName = name.trim()
    if (!trimmedCode) { setError('Please enter your access code.'); return }
    if (!trimmedName) { setError('Please enter your name.'); return }
    setStage('submitting')
    try {
      const validation = await validateAccessCode(trimmedCode)
      if (!validation) {
        setError('Code not recognised, or it has expired.')
        setStage('code')
        return
      }
      const { venue, access_code } = validation
      if (slug && venue.slug !== slug) {
        setError('That code is for a different venue.')
        setStage('code')
        return
      }
      const picker = await createPickerSession({
        venue_id: venue.id, session_type: 'shared_code',
        access_code_id: access_code.id, display_name: trimmedName,
      })
      setPickerSession({
        picker_id: picker.id, venue_id: venue.id, venue_slug: venue.slug,
        display_name: trimmedName, session_type: 'shared_code',
        started_at: new Date().toISOString(),
      })
      navigate(`/venue/${venue.slug}/digest`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? `Could not sign in: ${err.message}` : 'Could not sign in.')
      setStage('code')
    }
  }

  // ── NDA screen ─────────────────────────────────────────────────────────────

  if (stage === 'nda') {
    return <NDASplash venueName="Pacha Ibiza" onAccept={onNDAAccept} />
  }

  if (stage === 'role') {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center px-6 gap-3">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-white tracking-tight">How are you using this?</h2>
          <p className="text-white/40 text-sm mt-1">Sets what you see — change any time via the Role button</p>
        </div>
        {(Object.entries(ROLE_CONFIGS) as [VenueRole, typeof ROLE_CONFIGS[VenueRole]][]).map(([role, config]) => (
          <button key={role} type="button" onClick={() => onRoleSelect(role)}
            style={{
              width: '100%', maxWidth: '280px', padding: '16px 20px',
              borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)', cursor: 'pointer',
              textAlign: 'left', transition: 'all 0.15s',
            }}>
            <p style={{ color: 'white', fontWeight: 700, fontSize: '16px', margin: 0 }}>{config.label}</p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', margin: '4px 0 0 0' }}>{config.sub}</p>
          </button>
        ))}
      </div>
    )
  }

  // ── Code entry ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-room-bg flex items-center justify-center px-5 py-10">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-room-gold/5 rounded-full blur-[140px]" />
      </div>

      <form onSubmit={onSubmit} className="relative z-10 max-w-sm w-full space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-room-gold font-semibold mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-room-gold animate-pulse-dot" />
            Picker mode
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white">Sign in</h1>
          <p className="text-sm text-room-muted">Enter the access code issued by the venue.</p>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-1.5">Your name</span>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              disabled={stage === 'submitting'} autoComplete="name" autoCapitalize="words"
              placeholder="e.g. Maria R."
              className="w-full bg-room-surface border border-room-border rounded-xl px-4 py-3 text-base text-white placeholder:text-room-muted focus:outline-none focus:border-room-gold/60 disabled:opacity-50" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-1.5">Access code</span>
            <div className="relative">
              <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-room-muted pointer-events-none" />
              <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                disabled={stage === 'submitting'} autoComplete="one-time-code" autoCapitalize="characters"
                spellCheck={false} placeholder="PACHA-APR-2026"
                className="w-full bg-room-surface border border-room-border rounded-xl pl-11 pr-4 py-3 text-base text-white placeholder:text-room-muted font-mono tracking-wider focus:outline-none focus:border-room-gold/60 disabled:opacity-50" />
            </div>
          </label>
        </div>

        {error && (
          <div className="bg-room-error/15 border border-room-error/40 rounded-xl p-3.5 text-sm text-room-error">{error}</div>
        )}

        <button type="submit"
          disabled={stage === 'submitting' || !code.trim() || !name.trim()}
          className="w-full py-4 rounded-2xl font-bold text-base bg-white text-room-bg hover:bg-white/90 transition-all min-h-[48px] flex items-center justify-center gap-2 disabled:bg-room-surface disabled:text-room-muted disabled:cursor-not-allowed disabled:border disabled:border-room-border">
          {stage === 'submitting' ? 'Signing in…' : <><span>Enter</span><ArrowRight size={18} /></>}
        </button>

        <div className="text-center text-xs text-room-muted pt-2">
          <p className="mb-2">Don&rsquo;t have a code? Ask the venue admin.</p>
          <Link to="/enter" className="text-room-muted hover:text-white underline underline-offset-2">Go to creator sign-in</Link>
        </div>
      </form>
    </div>
  )
}
